/**
 * Fase da noite (§13.1.2): um só tipo e uma só função para "em que momento da noite estamos". Os rótulos de leitura de
 * §10.8.1 (`before_day`, `election_day`, …) se leem pela tabela de correspondência de §13.1.2; não são um tipo.
 * Pura: o computed `phase` de state/derived.ts (DE-B02) a chama com a vista exibida e o relógio da fonte.
 * `model/` não importa valores de `contracts/` (§2.2.2): as 27 UFs são as chaves de `view.places` sem 'ZZ'.
 */
import { T_OPEN } from './time';
import type { PlaceView, ResultsView } from './types';

export type NightPhase =
  /** relógio antes das 17h00 de 25/10 (inclui os dias anteriores) */
  | 'pre_open'
  /** 17h00 ou depois, nenhuma seção totalizada em lugar nenhum */
  | 'opening'
  /** só o Exterior (ZZ) tem seções totalizadas */
  | 'first_votes'
  /** alguma UF com seções e Brasil abaixo de 1% das seções */
  | 'early'
  /** Brasil com 1% ou mais, sem definição nacional */
  | 'counting'
  /** definição matemática ou resultado do TSE nacional, algo ainda apurando */
  | 'decided'
  /** Brasil 100% e todas as disputas de governador concluídas */
  | 'final';

/** 17h00 de 25/10 em t_min (§14.1). */
export const OPEN_T_MIN = T_OPEN;

/** Alguma das 27 UFs com seções totalizadas (ZZ fica de fora). */
export function ufWithData(view: ResultsView): boolean {
  for (const [code, p] of Object.entries(view.places) as Array<[string, PlaceView]>) {
    if (code !== 'ZZ' && p.sectionsFraction > 0) return true;
  }
  return false;
}

/** §13.1.2 (código da seção, sem desvio). */
export function nightPhase(view: ResultsView | null, clockTMin: number, governorsOn: boolean): NightPhase {
  if (clockTMin < OPEN_T_MIN) return 'pre_open';
  if (view === null) return 'opening';
  const n = view.national;
  const anyUf = ufWithData(view);
  const abroadWithData = (view.places.ZZ?.sectionsFraction ?? 0) > 0;
  if (n.sectionsFraction === 0 && !anyUf && !abroadWithData) return 'opening';
  const governorsDone =
    !governorsOn || Object.values(view.governor).every((g) => g !== undefined && g.status === 'complete');
  if (n.status === 'complete' && governorsDone) return 'final';
  if (n.mathDecision !== null || n.tseResult !== null) return 'decided';
  if (!anyUf) return 'first_votes';
  if (n.sectionsFraction < 0.01) return 'early';
  return 'counting';
}
